import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Zap, CheckCircle, Loader2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { API_URL } from "../config/api";

const VerifyTeamMember = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [verifying, setVerifying] = useState(true);
  const [status, setStatus] = useState(''); // verified, already_verified, invalid, error
  const [message, setMessage] = useState('');
  const [orgSlug, setOrgSlug] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const verify = async () => {
      if (!token) {
        setStatus('invalid');
        setMessage('No verification token provided');
        setVerifying(false);
        return;
      }

      try {
        const response = await axios.post(`${API_URL}/v2/verify-team-member`, {
          token
        });

        // Handle the new API response format
        if (response.data.success && response.data.emailVerified) {
          setStatus('verified');
          setMessage(response.data.message);
          toast.success('Email verified successfully!');
          // Redirect to set password page with token
          setTimeout(() => navigate(`/set-team-password?token=${token}`), 1000);
        } else if (response.data.alreadyVerified) {
          setStatus('already_verified');
          setMessage('Email was already verified');
          toast.success('Email already verified!');
          setTimeout(() => navigate('/login'), 1000);
        } else {
          setStatus(response.data.status || 'verified');
          setMessage(response.data.message);
          setOrgSlug(response.data.orgSlug || '');
        }
      } catch (error) {
        // Handle API errors with proper messages
        if (error.response?.status === 404) {
          setStatus('invalid');
          setMessage(error.response?.data?.detail || 'Invalid or expired verification token');
        } else {
          setStatus('error');
          setMessage(error.response?.data?.detail || 'An error occurred during verification');
        }
      } finally {
        setVerifying(false);
      }
    };

    verify();
  }, [token, navigate]);

  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{background: '#f8f9fa'}}>
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Verifying your email...</p>
        </div>
      </div>
    );
  }

  const isSuccess = status === 'verified' || status === 'already_verified';
  const iconBg = isSuccess ? '#10B981' : (status === 'invalid' ? '#EF4444' : '#F59E0B');
  const title = status === 'verified' 
    ? '✅ Email Verified!' 
    : status === 'already_verified'
    ? '✅ Already Verified!'
    : status === 'invalid'
    ? 'Invalid Link'
    : 'Verification Issue';
  const subtitle = status === 'verified'
    ? 'You can now login to your account'
    : status === 'already_verified'
    ? 'Your email was already verified - you\'re all set!'
    : status === 'invalid'
    ? 'This link is invalid or has expired'
    : 'Please contact your organization admin';

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{background: '#f8f9fa'}}>
      <Card className="w-full max-w-md shadow-xl relative overflow-hidden rounded-t-2xl rounded-b-none">
        <CardHeader className="text-center pt-16 pb-6">
          <div className="flex justify-center mb-4">
            <div className="p-4 rounded-xl shadow-lg" style={{background: iconBg}}>
              {isSuccess ? (
                <CheckCircle className="w-10 h-10 text-white" />
              ) : (
                <XCircle className="w-10 h-10 text-white" />
              )}
            </div>
          </div>
          <CardTitle className="text-3xl font-bold" style={{color: '#1a1a1a'}}>
            {title}
          </CardTitle>
          <p className="text-gray-600 mt-2">
            {subtitle}
          </p>
        </CardHeader>

        <CardContent className="px-8 pb-8">
          {isSuccess ? (
            <>
              <div className={`${isSuccess ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'} border rounded-lg p-4 mb-6`}>
                <p className={`text-sm ${isSuccess ? 'text-green-800' : 'text-yellow-800'} mb-3`}>
                  {status === 'verified' && '🎉 Email Verified!'}
                  {status === 'already_verified' && '✓ Already Verified!'}
                </p>
                <p className={`text-xs ${isSuccess ? 'text-green-700' : 'text-yellow-700'}`}>
                  {status === 'verified' 
                    ? 'Redirecting to set your password...'
                    : 'Redirecting to login...'
                  }
                </p>
              </div>

              <Button
                onClick={() => navigate('/login')}
                className="w-full h-12 text-base font-semibold"
                style={{background: '#2563EB'}}
              >
                Go to Login
              </Button>
            </>
          ) : (
            <>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-red-800 mb-2">
                  {status === 'invalid' ? '❌ Invalid or Expired Link' : '⚠️ Something Went Wrong'}
                </p>
                <p className="text-xs text-red-700">
                  {message || 'Please contact your organization admin for a new verification link.'}
                </p>
              </div>

              <Button
                onClick={() => navigate('/')}
                className="w-full h-12 text-base font-semibold"
                style={{background: '#2563EB'}}
              >
                Back to Home
              </Button>
            </>
          )}
        </CardContent>
        
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 text-center">
          <p className="text-xs text-gray-400">
            © {new Date().getFullYear()} BookingFlows. All rights reserved.
          </p>
        </div>
      </Card>
    </div>
  );
};

export default VerifyTeamMember;
